import React, { ReactElement } from 'react';
import { Type, TypeList, TypeListItem } from '@spotify-internal/encore-creator-web';
import { PODCAST_SUPPORT_EMAIL, CURRENT_SPEC_PDF } from '../../../lib/constants';
import { FAQ } from '../../../types';

export const FAQQuestions: FAQ[] = [
  {
    question: 'How do I get my podcast on Spotify?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          If you host your podcast with one of our aggregator partners, you can submit your podcast
          to Spotify in minutes. Find out how from your aggregator listed below:
        </Type>
        <ul>
          <li>
            <a href="https://learn.acast.com/en/articles/3404285-how-do-i-submit-my-podcast-to-spotify">
              Acast
            </a>
          </li>
          <li>
            <a href="https://help.anchor.fm/hc/en-us/articles/360003522412-Troubleshooting-podcast-distribution">
              Anchor
            </a>
          </li>
          <li>
            <a href="https://support.audioboom.com/hc/en-us/articles/360004455213-Submitting-to-Spotify">
              Audioboom
            </a>
          </li>
          <li>
            <a href="https://blog.backtracks.fm/how-to-submit-your-podcast-to-spotify-980b091e18a8">
              Backtracks
            </a>
          </li>
          <li>
            <a href="https://create.blubrry.com/manual/podcast-promotion/submit-podcast-to-spotify/">
              Blubrry
            </a>
          </li>
          <li>
            <a href="https://www.buzzsprout.com/help/59-submit-to-spotify">Buzzsprout</a>
          </li>
          <li>
            <a href="https://support.castos.com/castos-podcast-hosting/submit-your-podcast-to-spotify">
              Castos
            </a>
          </li>
          <li>
            <a href="https://help.captivate.fm/en/articles/3430043-how-to-get-your-podcast-on-spotify">
              Captivate
            </a>
          </li>
          <li>
            <a href="https://fireside.fm/distribution/spotify">Fireside</a>
          </li>
          <li>
            <a href="http://support.megaphone.fm/en/articles/1560879-how-do-i-distribute-my-podcast-on-apple-stitcher-google-spotify-pandora-and-other-platforms">
              Megaphone
            </a>
          </li>
          <li>
            <a href="https://support.libsyn.com/kb/spotify/">LibSyn</a>
          </li>
          <li>
            <a href="http://help.omnystudio.com/setting-up-a-podcast/adding-your-show-to-podcast-directories/submit-your-podcast-to-spotify">
              Omny
            </a>
          </li>
          <li>
            <a href="https://help.pinecast.com/knowledge-base/does-pinecast-support-spotify">
              Pinecast
            </a>
          </li>
          <li>
            <a href="http://help.podbean.com/support/solutions/articles/25000014128-how-to-submit-your-podbean-podcast-to-spotify">
              PodBean
            </a>
          </li>
          <li>
            <a href="https://www.podcaster.de/faq/antwort-52-Wie+melde+ich+meinen+Podcast+bei+Spotify+an%3F/">
              Podcaster.DE
            </a>
          </li>
          <li>
            <a href="https://podiant.co/blog/spotify/">Podiant</a>
          </li>
          <li>
            <a href="https://www.podigee.com/en/help/publish-podcast-on-spotify/">Podigee</a>
          </li>
          <li>
            <a href="http://www.podomatic.com/help/article/105">Podomatic</a>
          </li>
          <li>
            <a href="https://support.redcircle.com/en/articles/2756337-distributing-your-redcircle-podcast-to-spotify">
              RedCircle
            </a>
          </li>
          <li>
            <a href="https://help.simplecast.com/publishing-your-podcast/how-to-get-your-podcast-on-spotify">
              Simplecast
            </a>
          </li>
          <li>
            <a href="https://help.spreaker.com/kb/sharing-and-distribution/distributing-your-podcast-on-spotify">
              Spreaker
            </a>
          </li>
          <li>
            <a href="https://help.transistor.fm/article/9-how-do-i-submit-my-show-to-spotify">
              Transistor
            </a>
          </li>
          <li>
            <a href="http://support.whooshkaa.com/support/solutions/articles/9000154386-how-to-submit-your-show-to-spotify">
              Whooshkaa
            </a>
          </li>
        </ul>
        <Type as="p" variant={Type.body1}>
          If you host with <strong>Art19</strong>, <strong>Ausha</strong>,{' '}
          <strong>Cast Plus</strong>, or <strong>Zencast</strong>, you'll need to reach out to your
          aggregator for more information on how you can authorize Spotify as an additional
          distribution platform.
        </Type>
        <Type as="h1" variant={Type.heading4}>
          Upload a podcast with Spotify for Podcasters
        </Type>
        <Type as="p" variant={Type.body1}>
          <b>Note:</b> Spotify doesn’t host podcasts. You need to provide a link to an RSS feed in
          order to have your podcast on Spotify.
        </Type>
        <Type as="p" variant={Type.body1}>
          <a href="https://accounts.spotify.com/login?continue=https%3A%2F%2Fpodcasters.spotify.com%2Fgateway">
            Log in to Spotify for Podcasters
          </a>{' '}
          with your Spotify account, or choose <b>SIGN UP</b> to create one. Then, follow these
          steps:
        </Type>
        <TypeList.ol>
          <TypeListItem variant={Type.body1}>
            Click <strong>GET STARTED.</strong>
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Paste in the link to your podcast's RSS feed.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            We’ll now send a verification email to the address in your RSS feed. Copy the 8-digit
            code from that email and paste in the submit form.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Add podcast info like the category, language, and country.
          </TypeListItem>
        </TypeList.ol>
      </>
    ),
  },
  {
    question: 'How do I get access to Spotify for Podcasters?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          To get access, you need to{' '}
          <a href="https://accounts.spotify.com/login?continue=https%3A%2F%2Fpodcasters.spotify.com%2Fgateway">
            log in to Spotify for Podcasters with your Spotify account
          </a>
          , or choose <b>SIGN UP</b> to create one. Then, follow these steps:
        </Type>
        <TypeList.ol>
          <TypeListItem variant={Type.body1}>
            Open the side menu by clicking the three dots in the top-left.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Click <strong>Add or claim podcast</strong>.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Click <strong>GET STARTED.</strong>
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Paste in the link to your podcast's RSS feed.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            We’ll now send a verification email to the address in your RSS feed. Copy the 8-digit
            code from that email and paste in the submit form.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Add podcast info like the category, language, and country.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>Review the details and submit.</TypeListItem>
        </TypeList.ol>

        <Type as="p" variant={Type.body1}>
          Multiple team members can get access using the steps above to view stats for the same
          podcast. Each team member will need their own Spotify account and access to the email
          address in the RSS feed before getting access to the podcast.
        </Type>
        <Type as="p" variant={Type.body1}>
          <b>Note:</b> If you claim or upload your podcast with the wrong Spotify account and want
          to change which account has access to Spotify for Podcasters, the easiest thing to do is
          log in to the correct Spotify account, then claim access again following the steps above.
        </Type>
      </>
    ),
  },
  {
    question: 'What is Spotify’s policy on submitting music via Spotify for Podcasters?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          We want to ensure that creators are using the appropriate channels to share their content
          with our users. Spotify for Podcasters should not be used to distribute music tracks, DJ
          mixes, or similar musical content. Spotify reserves the right to remove podcasts that
          violate this policy, regardless of the licensing status of your music.
        </Type>
        <Type as="p" variant={Type.body1}>
          If you’re a creator looking to get your music on Spotify, you can check out our{' '}
          <a href="https://artists.spotify.com/faq/music#how-do-i-get-my-music-on-spotify">
            How do I get music on Spotify?
          </a>{' '}
          FAQ on Spotify for Artists. You can also use one of our{' '}
          <a href="https://artists.spotify.com/directory/distribution">preferred providers</a> to
          distribute your music.
        </Type>
      </>
    ),
  },
  {
    question: 'How often is my podcast data updated?',
    answer: (
      <Type as="p" variant={Type.body1}>
        Your podcast data’s updated daily. When you add new podcasts or episodes to Spotify, their
        data will show up one day later.{' '}
      </Type>
    ),
  },
  {
    question: 'Can I export a report with my data?',
    answer: (
      <Type as="p" variant={Type.body1}>
        Yep! You can download a .CSV file with your data anywhere you see an arrow next to the date
        range.
      </Type>
    ),
  },
  {
    question: 'How are starts and streams counted?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          Starts measure any listener who starts listening to a podcast episode. There’s no minimum
          time limit to be counted as a start on Spotify.
        </Type>
        <Type as="p" variant={Type.body1}>
          Streams are counted in Spotify for Podcasters when a podcast’s streamed for over 60
          seconds.{' '}
        </Type>
      </>
    ),
  },
  {
    question: 'Who are my listeners and followers?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          Listeners are unique listeners who started an episode in your catalog.
        </Type>
        <Type as="p" variant={Type.body1}>
          Followers are listeners who hit Follow on your podcast on Spotify. They automatically
          receive new episodes of your podcast.
        </Type>
      </>
    ),
  },
  {
    question: 'How do you calculate average listen?',
    answer: (
      <Type as="p" variant={Type.body1}>
        The average listen shows how long people listen to your podcast episode for on average. To
        get this number, we average the data for at least 50% of listeners.
      </Type>
    ),
  },
  {
    question: 'How is the data calculated for Artists they listen to?',
    answer: (
      <Type as="p" variant={Type.body1}>
        We look at people who listened to the podcast at least once in the past 28 days, then
        calculate the five artists they listen to most on Spotify.
      </Type>
    ),
  },
  {
    question: 'How can I add a trailer for my podcast?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          Your podcast trailer appears at the top of the episode list. All other episodes appear in
          the chronological episode list.
        </Type>
        <Type as="p" variant={Type.body1}>
          You can set a podcast episode as a trailer in Spotify for Podcasters:
        </Type>
        <TypeList.ol>
          <TypeListItem variant={Type.body1}>Click on a podcast.</TypeListItem>
          <TypeListItem variant={Type.body1}>
            Head to <strong>DETAILS</strong>.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Click <strong>SET TRAILER</strong>. <br />
            Note: If you already have a trailer, click <strong>MANAGE TRAILER</strong> to choose a
            new trailer.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Pick an episode, then click <strong>SET TRAILER</strong>.
          </TypeListItem>
        </TypeList.ol>

        <Type as="p" variant={Type.body1}>
          Once you add a podcast trailer, it should show in Spotify within minutes. <br />
          Note: It’s only possible to see podcast trailers in the mobile Spotify app.
        </Type>
      </>
    ),
  },
  {
    question: 'How can I change or remove a podcast trailer?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          We automatically show the first episode set as a trailer on your RSS feed.
        </Type>
        <Type as="p" variant={Type.body1}>
          To change or remove your trailer in Spotify for Podcasters:
        </Type>
        <TypeList.ol>
          <TypeListItem variant={Type.body1}>Click on a podcast.</TypeListItem>
          <TypeListItem variant={Type.body1}>
            Go to <strong>DETAILS</strong>.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Click the 3 dots ‘...’ next to the trailer.
          </TypeListItem>
          <TypeList.ul>
            <TypeListItem variant={Type.body1}>
              To remove the trailer, click <b>Remove trailer</b>.
            </TypeListItem>
            <TypeListItem variant={Type.body1}>
              To change the trailer, click <b>Manage trailer</b> and follow the steps until you see
              your new trailer’s been set.
            </TypeListItem>
          </TypeList.ul>
        </TypeList.ol>
      </>
    ),
  },
  {
    question: 'Can I link to particular points of my podcast?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          Yep! You can use timestamps in your episode descriptions so listeners can start playing
          from precise moments. Just include them in parentheses in your episode description and
          they’ll automatically link.
        </Type>
        <Type as="p" variant={Type.body1}>
          You can use any of the following formats: <code>(m:ss)</code>; <code>(mm:ss)</code>;{' '}
          <code>(mmm:ss)</code>; <code>(h:mm:ss)</code>; <code>(hh:mm:ss)</code>; or{' '}
          <code>(hhh:mm:ss)</code>.
        </Type>
        <Type as="p" variant={Type.body1}>
          For example: <code>(10:20)</code> - will automatically link to 10 minutes and 20 seconds
          into your episode.{' '}
        </Type>
        <Type as="p" variant={Type.body1}>
          You can also use a combination to express a range: <code>(hh:mm:ss-hh:mm:ss)</code>.
        </Type>
        <Type as="p" variant={Type.body1}>
          <strong>Note:</strong> Timestamps are currently only clickable on mobile, and timestamps
          greater than the episode's length won’t be clickable.
        </Type>
      </>
    ),
  },
  {
    question: 'Can I add links to my episode descriptions?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          Yes, if it's a link to something on Spotify (e.g. a playlist or another episode on
          Spotify), it takes the listener there without disrupting what’s playing.
        </Type>
        <Type as="p" variant={Type.body1}>
          Any other valid URL takes the listener to the linked page in their preferred web browser,
          and any linked email addresses takes them to their email client. <br />
          e.g. <code>Listen to podcasts on https://www.spotify.com</code>
        </Type>
        <Type as="p" variant={Type.body1}>
          <strong>Note:</strong> Links are currently only clickable in the mobile app.
        </Type>
      </>
    ),
  },
  {
    question:
      'When I add a link to my podcast’s RSS feed, I get a ‘missing elements’ error message.',
    answer: (
      <Type as="p" variant={Type.body1}>
        Reach out to your hosting platform to see if they can help update the missing elements
        (e.g., cover art, title, description, etc.). We also have a{' '}
        <a href={`${CURRENT_SPEC_PDF}`}>Spotify podcast spec doc</a> that you can refer to.
      </Type>
    ),
  },
  {
    question: 'How do I update my podcast’s RSS feed link or hosting provider info?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          You can view and update the current link to your podcast’s RSS feed and hosting provider
          in Spotify for Podcasters:
        </Type>
        <TypeList.ol>
          <TypeListItem variant={Type.body1}>Click on a podcast.</TypeListItem>
          <TypeListItem variant={Type.body1}>
            Head to <strong>DETAILS.</strong>
            <br />
            Here you can see the current link to your podcast’s RSS feed and hosting partner.
          </TypeListItem>

          <TypeListItem variant={Type.body1}>
            Click <strong>UPDATE.</strong>
          </TypeListItem>

          <TypeListItem variant={Type.body1}>Enter the new link to your RSS feed.</TypeListItem>
          <TypeListItem variant={Type.body1}>
            Confirm the hosting provider and click <strong>SUBMIT</strong>.
          </TypeListItem>
        </TypeList.ol>
        <Type as="p" variant={Type.body1}>
          Don’t have access to Spotify for Podcasters? Contact{' '}
          <a href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>{PODCAST_SUPPORT_EMAIL}</a> for help.
        </Type>
      </>
    ),
  },
  {
    question: 'I updated the audio/image in a podcast but it’s not updating on Spotify.',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          We recommend saving the new file under a slightly different name so our system recognizes
          the change. Once done, the update should reflect in the app within a few hours.
        </Type>
        <Type as="p" variant={Type.body1}>
          If you’ve updated your RSS feed, you can update the current link to your podcast’s RSS
          feed in Spotify for Podcasters. Check out{' '}
          <a href="/faq#how-do-i-update-the-link-to-my-podcasts-rss-feed-with-spotify-for-podcasters">
            How do I update my podcast’s RSS feed link or hosting provider info
          </a>
          ?
        </Type>
      </>
    ),
  },
  {
    question: 'Why isn’t my podcast appearing in Search?',
    answer: (
      <Type as="p" variant={Type.body1}>
        New podcasts can take a little time to appear in Search, but they usually appear after a few
        hours.
      </Type>
    ),
  },
  {
    question: 'Can I have video episodes in my RSS feed?',
    answer: (
      <Type as="p" variant={Type.body1}>
        We don’t currently support video episodes in a podcast.
      </Type>
    ),
  },
  {
    question: 'Do you have a spec doc?',
    answer: (
      <Type as="p" variant={Type.body1}>
        Yes! You can access our spec doc <a href={`${CURRENT_SPEC_PDF}`}>here</a>.
      </Type>
    ),
  },
  {
    question: 'The email address to verify my podcast is wrong. How can I update it?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          This email address is pulled from the RSS feed for your podcast. We rely on email
          verification to ensure you’re the owner of a podcast.
        </Type>
        <Type as="p" variant={Type.body1}>
          Reach out to your hosting partner to see if you can update the email address associated
          with your RSS feed. If your podcast's hosted with Anchor,{' '}
          <a href="https://anch.co/anchorS4Phelp">follow these steps to update your email</a>.
        </Type>
        <Type as="p" variant={Type.body1}>
          It may take a couple hours for your public RSS feed to update, and for Spotify to
          recognize those updates.
        </Type>
        <Type as="p" variant={Type.body1}>
          If your RSS feed’s been updated, and it’s been over 4 hours, contact{' '}
          <a href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>{PODCAST_SUPPORT_EMAIL}</a> for help. To get
          help faster, include the name of your podcast, the link to your RSS feed, and the email
          address of your Spotify account.
        </Type>
      </>
    ),
  },
  {
    question: 'My podcast categories look wrong.',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          A few factors affect the categories associated to your podcast:
        </Type>
        <TypeList.ul>
          <TypeListItem variant={Type.body1}>
            The <strong>&lt;itunes:categories&gt;</strong> field in your RSS feed.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            If you upload your podcast through Spotify for Podcasters, you can choose categories
            when you submit your podcast.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Our editorial team also curate podcast categories.
          </TypeListItem>
        </TypeList.ul>
        <Type as="p" variant={Type.body1}>
          It’s not possible to change your podcast categories. We hope to have this feature
          available to you in the future!
        </Type>
      </>
    ),
  },
  {
    question: 'How can I promote my podcast on Spotify?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          With over 200 million listeners around the world, Spotify is a great place to promote your
          podcast. Here are some tips.
        </Type>
        <Type as="h2" variant={Type.heading4}>
          Join Spotify for Podcasters
        </Type>
        <Type as="p" variant={Type.body1}>
          Spotify for Podcasters makes it easy for you to upload your podcast to Spotify, and see
          the data and insights you need to grow your audience.
        </Type>

        <Type as="p" variant={Type.body1}>
          Just click Log In at the top-right of this page to get started.
        </Type>
        <Type as="h2" variant={Type.heading4}>
          Build your followers
        </Type>
        <Type as="p" variant={Type.body1}>
          Encourage your fans to follow your podcast on Spotify. New episodes are automatically
          saved in their Library, and you’ll also be featured in the personalized sections of their
          app.
        </Type>
        <TypeList.ul>
          <TypeListItem variant={Type.body1}>
            <a href="/faq#how-can-i-share-my-podcast">Share your podcast</a> on social channels.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            <a href="/faq#how-do-i-embed-a-follow-button-on-my-website">Embed a follow button</a> on
            your website.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Let fans know your podcast’s on Spotify. <br /> Use our{' '}
            <a href="https://developer.spotify.com/branding-guidelines/">Branding Guidelines</a> to
            find how best to use our logo, icon, colours, fonts, messaging, widgets, and more.{' '}
            <br />
            <b>Note</b>: Spotify has a unique visual identity which you shouldn’t copy. Instead, use
            your own visual designs and incorporate our latest logos where appropriate.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            <a href="https://podcasters.spotify.com/faq#can-i-add-links-to-my-episode-descriptions">
              Add links to your episode description
            </a>
            .
          </TypeListItem>
        </TypeList.ul>
        <Type as="h2" variant={Type.heading4}>
          Promote your podcast with tailored audio ads
        </Type>
        <Type as="p" variant={Type.body1}>
          Spotify Ad Studio lets you create and manage audio campaigns in minutes, with options for
          different sized budgets. Simply submit a script and pick a background track, and we
          produce the ad complete with recorded voiceover.
        </Type>

        <Type as="p" variant={Type.body1}>
          <a href="https://adstudio.spotify.com/?utm_source=s4a">Get started here</a>.
        </Type>

        <Type as="p" variant={Type.body1}>
          Note: This is currently only available in some countries. For information about
          advertising elsewhere, check out{' '}
          <a href="https://spotifyforbrands.com/">Spotify for Brands</a>.
        </Type>
      </>
    ),
  },
  {
    question: 'How can I get my podcast in a Spotify playlist?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          We're not currently accepting submissions to podcast playlists.{' '}
        </Type>

        <Type as="p" variant={Type.body1}>
          Our Editorial team uses taste, data, research, and trends to create these playlists. Check
          out <a href="/faq#how-can-i-promote-my-podcast-on-spotify">our promotion guide</a> for
          hints and tips on how to build your podcast listeners on Spotify.
        </Type>
      </>
    ),
  },
  {
    question: 'How can I share my podcast?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          If you have access to Spotify for Podcasters, find your podcast link and shareable badges
          in the <b>DETAILS</b> tab. To share directly from the Spotify app, check out our guide on
          how to{' '}
          <a href="https://support.spotify.com/article/sharing-music/">
            share music, podcasts, and videos
          </a>
          .
        </Type>
      </>
    ),
  },
  {
    question: 'How do I embed a follow button on my website?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          Help fans find and follow your podcasts by embedding a follow button in your website.
        </Type>
        <TypeList.ol>
          <TypeListItem variant={Type.body1}>
            Go to your podcast page on the Spotify app.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>Click (…) next to the FOLLOW button.</TypeListItem>
          <TypeListItem variant={Type.body1}>
            Click <b>Share</b>, then <b>Copy Embed Code</b>.
          </TypeListItem>
          <TypeListItem variant={Type.body1}>
            Paste the code in your website. It works with any website that supports HTML editing.
            For more info, check out{' '}
            <a href="https://developer.spotify.com/documentation/widgets/guides/adding-a-spotify-follow-button/">
              Spotify for Developers
            </a>
            .
          </TypeListItem>
        </TypeList.ol>

        <Type as="p" variant={Type.body1}>
          Those who click the follow button instantly follow your podcast. New episodes are
          automatically saved in their Library, and you’ll also be featured in the personalized
          sections of their app.
        </Type>
      </>
    ),
  },
  {
    question: 'How can I contact the Spotify for Podcasters team?',
    answer: (
      <>
        <Type as="p" variant={Type.body1}>
          Whether you need help or have feedback for Spotify for Podcasters, we’re all ears!
        </Type>
        <Type as="p" variant={Type.body1}>
          Feel free to reach out to your account manager or to{' '}
          <a href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>{PODCAST_SUPPORT_EMAIL}</a>. If you need help,
          include the name of your podcast, the link to your RSS feed, and the email address of your
          Spotify account to get help faster.
        </Type>
      </>
    ),
  },
];
